/**
 * superadminStripeModeService -- the SuperAdmin read/write surface for a
 * distributor's Stripe PLATFORM MODE (dual-mode unit 5).
 *
 * Backs `api/src/endpoints/superadmin_distributors.py`:
 *
 *   GET   /api/superadmin/distributors/:id/stripe-platform-mode
 *   PATCH /api/superadmin/distributors/:id/stripe-platform-mode
 *
 * Both are SA-only (`superadmin.settings`); a non-SA caller gets 404, not
 * 403, matching that blueprint's existence-oracle posture.
 *
 * WHY A DEDICATED READ rather than reading the distributor resource.
 * `stripe_platform_mode` is in `Distributor.exclude_attrs`, so SAFRS does not
 * serialize it on `GET /api/distributors/:id/` at all -- the column decides
 * whether REAL MONEY moves, and a client-writable copy would let a
 * distributor flip itself onto the live platform. This endpoint is the only
 * way the SA page can see the value.
 *
 * Conventions match superadminPricingService: shared `@/axios` instance, full
 * `/api/...` paths, methods return the axios promise.
 */
import axios from "@/axios";

function modeUrl(distributorId) {
    return `/api/superadmin/distributors/${distributorId}/stripe-platform-mode`;
}

/**
 * Read the distributor's platform mode AND whether a Stripe account is
 * currently connected.
 *
 * Response: { id, name, stripe_platform_mode, stripe_account_connected }
 *
 * The two travel together deliberately. Whether to LOCK the control and
 * whether the endpoint will REFUSE the write are the same question, and
 * deriving them from two reads is how the UI and the guard drift apart --
 * the drift shows up as a control that looks usable and 409s.
 *
 * `stripe_account_connected` is a boolean, never the `acct_` id: the page
 * needs to know THAT an account is attached, not WHICH.
 */
export function getStripePlatformMode(distributorId) {
    return axios.get(modeUrl(distributorId));
}

/**
 * Change which ThreadFlow Stripe platform this distributor transacts on.
 *
 * `mode` is 'sandbox' or 'live' (src/models/distributor.STRIPE_PLATFORM_MODES).
 *
 * REJECTS WITH 409 when a Stripe account is still connected. That is not an
 * error to paper over -- it is the safety rule. Changing the platform under a
 * connected account leaves a live `acct_` id addressed with a sandbox key (or
 * the reverse), which does not fail here; it fails later at a real charge.
 * The required order is: disconnect the Stripe account, change the mode, then
 * have the distributor re-run Connect OAuth.
 *
 * Callers must surface the 409 distinctly from a 403 -- "you lack permission"
 * and "disconnect the account first" lead to completely different next steps.
 *
 * ALSO REJECTS WITH 409 `{ error: "uncollectable_orders",
 * uncollectable_order_count }` when the flip would strand unpaid orders whose
 * saved card lives on the current platform. That one is a WARNING, not a
 * refusal: re-send with `confirmCount` set to the count the server returned.
 * The count, not a boolean, so the admin consents to the number they saw.
 */
export function setStripePlatformMode(distributorId, mode, confirmCount) {
    const body = { stripe_platform_mode: mode };
    if (confirmCount !== undefined) {
        body.confirm_uncollectable_count = confirmCount;
    }
    return axios.patch(modeUrl(distributorId), body);
}

/*
 * Stripe connection park/switch (the SA "Stripe connections" card).
 *
 * Backs the stripe-bindings endpoints, which wrap
 * `api/src/services/stripe_binding_park.py`. A park saves the distributor's
 * current Connect binding and clears it onto the other platform; a switch
 * restores a saved binding. Neither touches Stripe's side of the
 * authorization, which is why they exist next to the mode PATCH above: that
 * one refuses while an account is connected, these move the account with
 * the mode.
 *
 * Every write takes `dryRun`. The card always sends a dry run first and shows
 * the returned `changes` before it sends the real one. A refusal is 409
 * `{ error, message, stranded_order_count? }` and nothing was written.
 */
function bindingsUrl(distributorId) {
    return `/api/superadmin/distributors/${distributorId}/stripe-bindings`;
}

/**
 * Response: { current: { mode, account_masked, readiness, readiness_source },
 *             saved: [{ mode, account_masked, saved_at, saved_by }] }
 */
export function getStripeBindings(distributorId) {
    return axios.get(bindingsUrl(distributorId));
}

export function parkStripeBinding(distributorId, dryRun) {
    return axios.post(`${bindingsUrl(distributorId)}/park`, {
        dry_run: dryRun,
    });
}

export function switchStripeBinding(distributorId, to, dryRun) {
    return axios.post(`${bindingsUrl(distributorId)}/switch`, {
        to,
        dry_run: dryRun,
    });
}
